/** Formatting helpers for the Accounts Receivable & Dunning page. */

import type { AgingBucket } from '../types/arDunning';

export function formatMoney(value: number | null | undefined, currency = 'EUR'): string {
    if (value === null || value === undefined || isNaN(Number(value))) return '–';
    return new Intl.NumberFormat('de-CH', {
        style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(Number(value));
}

/** Compact money for KPI tiles (e.g. € 1.2M). */
export function formatMoneyShort(value: number | null | undefined, currency = 'EUR'): string {
    if (value === null || value === undefined || isNaN(Number(value))) return '–';
    return new Intl.NumberFormat('de-CH', {
        style: 'currency', currency, notation: 'compact', maximumFractionDigits: 1,
    }).format(Number(value));
}

// formatDate / formatDateTime moved to utils/format.ts so that shared components under
// components/ui/ (CommentsThread) can use them without importing an AR-Dunning util.
// Re-exported here unchanged — every existing AR import keeps working.
export { formatDate, formatDateTime } from './format';

/** Local (CET) yyyy-mm-dd — never toISOString (UTC shift would roll the date). */
export function todayIso(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export const AGING_BUCKETS: { key: AgingBucket; label: string }[] = [
    { key: 'current', label: 'Current' },
    { key: '1-30', label: '1–30' },
    { key: '31-60', label: '31–60' },
    { key: '61-90', label: '61–90' },
    { key: '91-180', label: '91–180' },
    { key: '181-365', label: '181–365' },
    { key: '365+', label: '365+' },
    { key: 'unknown', label: 'No due date' },
];

/** Overdue buckets only (excludes current/unknown) — for the aging matrix columns. */
export const OVERDUE_BUCKETS: { key: AgingBucket; label: string }[] = AGING_BUCKETS.filter(
    b => b.key !== 'current' && b.key !== 'unknown',
);

const AGING_STYLES: Record<string, string> = {
    current: 'text-emerald-700',
    '1-30': 'text-amber-700',
    '31-60': 'text-orange-700',
    '61-90': 'text-rose-700',
    '91-180': 'text-rose-800',
    '181-365': 'text-rose-900',
    '365+': 'text-rose-900 font-semibold',
    unknown: 'text-slate-400',
};

export function agingClass(bucket: string): string {
    return AGING_STYLES[bucket] ?? 'text-slate-600';
}

export function agingBarColor(bucket: string): string {
    const map: Record<string, string> = {
        current: 'bg-emerald-400',
        '1-30': 'bg-amber-400',
        '31-60': 'bg-orange-400',
        '61-90': 'bg-rose-400',
        '91-180': 'bg-rose-500',
        '181-365': 'bg-rose-600',
        '365+': 'bg-rose-800',
        unknown: 'bg-slate-300',
    };
    return map[bucket] ?? 'bg-slate-400';
}

export function bucketLabel(bucket: string): string {
    return AGING_BUCKETS.find(b => b.key === bucket)?.label ?? bucket;
}

/** Plain grouped number (no currency symbol) — for {TotalOpenAmount} substitution. */
export function formatAmountPlain(value: number | null | undefined): string {
    if (value === null || value === undefined || isNaN(Number(value))) return '0.00';
    return new Intl.NumberFormat('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value));
}

/**
 * Map a BC/etl LanguageCode (DES/DEU/FRA/ITA/ENU) OR a 2-letter override to a
 * template language. Mirrors the backend templateLanguage() so the frontend picks
 * the same template the seed matrix provides.
 */
export function templateLanguage(languageCode: string | null | undefined): 'DE' | 'FR' | 'IT' | 'EN' {
    const c = (languageCode || '').toUpperCase();
    if (c.startsWith('DE')) return 'DE';
    if (c.startsWith('FR')) return 'FR';
    if (c.startsWith('IT')) return 'IT';
    return 'EN';
}

/** Derive a display name from an e-mail local part ("roman.dill@x" -> "Roman Dill"). */
export function senderNameFromEmail(email: string | null | undefined): string {
    if (!email) return '';
    const local = email.split('@')[0] || '';
    return local
        .split(/[._-]+/)
        .filter(Boolean)
        .map(p => p.charAt(0).toUpperCase() + p.slice(1))
        .join(' ') || email;
}

// ─── Payment-behaviour risk (Phase 7) ───────────────────────────────────────
import type { ArRiskLevel } from '../types/arDunning';

export const RISK_LABEL: Record<ArRiskLevel, string> = {
    red: 'High risk',
    amber: 'Watch',
    green: 'Low risk',
    none: 'No history',
};

/** Dot colour for the risk badge. */
export function riskDotClass(level: ArRiskLevel): string {
    switch (level) {
        case 'red': return 'bg-rose-500';
        case 'amber': return 'bg-amber-400';
        case 'green': return 'bg-emerald-500';
        default: return 'bg-slate-300';
    }
}

/** Pill styling for the risk badge. */
export function riskPillClass(level: ArRiskLevel): string {
    switch (level) {
        case 'red': return 'bg-rose-100 text-rose-800';
        case 'amber': return 'bg-amber-100 text-amber-800';
        case 'green': return 'bg-emerald-100 text-emerald-700';
        default: return 'bg-slate-100 text-slate-500';
    }
}

/** Monotonic sort value: red > amber > green > none, ties broken by score. */
export function riskSortValue(level: ArRiskLevel, score: number): number {
    const rank = level === 'red' ? 3 : level === 'amber' ? 2 : level === 'green' ? 1 : 0;
    return rank * 1000 + score;
}

/** Signed delay days, e.g. "12d late" / "3d early" / "on time". */
export function formatDelayDays(avg: number | null | undefined): string {
    if (avg === null || avg === undefined || isNaN(Number(avg))) return '–';
    const n = Math.round(Number(avg));
    if (n > 0) return `${n}d late`;
    if (n < 0) return `${Math.abs(n)}d early`;
    return 'on time';
}

/** Dunning-level pill styling (0 = none). */
export function levelBadgeClass(level: number): string {
    switch (level) {
        case 1: return 'bg-amber-100 text-amber-800';
        case 2: return 'bg-orange-100 text-orange-800';
        case 3: return 'bg-rose-100 text-rose-800';
        default: return 'bg-slate-100 text-slate-600';
    }
}
